import { useState } from "react";
import { toast } from "sonner";
import { EnvelopeSimple, Phone, WhatsappLogo, PaperPlaneTilt, CircleNotch } from "@phosphor-icons/react";
import { PageHero } from "../components/Sections";
import { Reveal, Overline } from "../components/Reveal";
import { createLead, createCallBooking } from "../lib/api";
import { useSettings } from "../hooks/usePublicContent";
import { BRAND } from "../data/content";

const inputCls =
  "w-full bg-[#0b0b0d] border border-white/10 text-white rounded-sm px-4 py-3 focus:outline-none focus:border-[#D97736] focus:ring-1 focus:ring-[#D97736] transition-colors placeholder:text-[#52525b]";
const labelCls = "block text-sm font-medium text-[#F8F8F8] mb-2";

function Field({ label, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

function PreventivoForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service_type: "consulenza", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createLead(form);
      toast.success("Richiesta inviata! Ti ricontatto al più presto.");
      setForm({ name: "", email: "", phone: "", service_type: "consulenza", message: "" });
    } catch {
      toast.error("Ops, qualcosa è andato storto. Riprova o scrivimi su WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="preventivo-form" className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Nome e cognome *">
          <input data-testid="preventivo-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Mario Rossi" />
        </Field>
        <Field label="Email *">
          <input data-testid="preventivo-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="mario@email.com" />
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Telefono">
          <input data-testid="preventivo-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+39 ..." />
        </Field>
        <Field label="Di cosa hai bisogno?">
          <select data-testid="preventivo-service" value={form.service_type} onChange={set("service_type")} className={inputCls}>
            <option value="consulenza">Consulenza</option>
            <option value="formazione">Formazione</option>
            <option value="evento">Evento Open Bar</option>
            <option value="generico">Altro</option>
          </select>
        </Field>
      </div>
      <Field label="Messaggio *">
        <textarea data-testid="preventivo-message" required rows={5} value={form.message} onChange={set("message")} className={inputCls} placeholder="Raccontami il tuo progetto, il locale o l'evento..." />
      </Field>
      <button data-testid="preventivo-submit" disabled={loading} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#D97736] text-white px-8 py-4 rounded-sm font-semibold hover:bg-[#B85B22] transition-all disabled:opacity-60">
        {loading ? <CircleNotch size={20} className="animate-spin" /> : <PaperPlaneTilt size={20} />}
        {loading ? "Invio..." : "Invia richiesta"}
      </button>
    </form>
  );
}

function CallForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_date: "", preferred_time: "", topic: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createCallBooking(form);
      toast.success("Richiesta di call inviata! Ti confermo lo slot al più presto.");
      setForm({ name: "", email: "", phone: "", preferred_date: "", preferred_time: "", topic: "", message: "" });
    } catch {
      toast.error("Ops, qualcosa è andato storto. Riprova o scrivimi su WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="call-form" className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Nome e cognome *">
          <input data-testid="call-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Mario Rossi" />
        </Field>
        <Field label="Email *">
          <input data-testid="call-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="mario@email.com" />
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Telefono">
          <input data-testid="call-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+39 ..." />
        </Field>
        <Field label="Argomento">
          <input data-testid="call-topic" value={form.topic} onChange={set("topic")} className={inputCls} placeholder="Consulenza, formazione, evento..." />
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Data preferita">
          <input data-testid="call-date" type="date" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} />
        </Field>
        <Field label="Orario preferito">
          <input data-testid="call-time" type="time" value={form.preferred_time} onChange={set("preferred_time")} className={inputCls} />
        </Field>
      </div>
      <Field label="Note">
        <textarea data-testid="call-message" rows={4} value={form.message} onChange={set("message")} className={inputCls} placeholder="Cosa vorresti discutere durante la call?" />
      </Field>
      <button data-testid="call-submit" disabled={loading} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#D97736] text-white px-8 py-4 rounded-sm font-semibold hover:bg-[#B85B22] transition-all disabled:opacity-60">
        {loading ? <CircleNotch size={20} className="animate-spin" /> : <PaperPlaneTilt size={20} />}
        {loading ? "Invio..." : "Prenota la call"}
      </button>
    </form>
  );
}

export default function Contatti() {
  const [tab, setTab] = useState("preventivo");
  const s = useSettings();

  return (
    <div data-testid="contatti-page">
      <PageHero
        overline="Contatti"
        title="Parliamone. Ti ricontatto io con una proposta su misura."
        subtitle="Richiedi un preventivo o prenota una call conoscitiva. Le richieste mi arrivano subito via email e WhatsApp."
      />

      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Left info */}
          <Reveal className="lg:col-span-1">
            <div className="bg-[#111113] border border-white/5 rounded-sm p-8 sticky top-28">
              <Overline>Contatti diretti</Overline>
              <h3 className="mt-4 font-display text-2xl font-semibold">Preferisci scrivere?</h3>
              <p className="mt-3 text-[#A1A1AA] text-sm leading-relaxed">Sono a disposizione per qualsiasi domanda. Rispondo personalmente.</p>
              <div className="mt-8 space-y-4">
                <a href={`https://wa.me/${s.whatsapp}`} target="_blank" rel="noreferrer" data-testid="contatti-whatsapp" className="flex items-center gap-4 p-4 rounded-sm bg-[#0b0b0d] border border-white/5 hover:border-[#25D366]/50 transition-colors group">
                  <WhatsappLogo size={26} className="text-[#25D366]" />
                  <div>
                    <div className="text-sm text-[#A1A1AA]">WhatsApp</div>
                    <div className="font-medium group-hover:text-[#25D366] transition-colors">{s.phone}</div>
                  </div>
                </a>
                <a href={`mailto:${s.email}`} data-testid="contatti-email" className="flex items-center gap-4 p-4 rounded-sm bg-[#0b0b0d] border border-white/5 hover:border-[#D97736]/50 transition-colors group">
                  <EnvelopeSimple size={26} className="text-[#D97736]" />
                  <div>
                    <div className="text-sm text-[#A1A1AA]">Email</div>
                    <div className="font-medium group-hover:text-[#D97736] transition-colors break-all">{s.email}</div>
                  </div>
                </a>
                <a href={`tel:${s.phone.replace(/\s/g, "")}`} data-testid="contatti-phone" className="flex items-center gap-4 p-4 rounded-sm bg-[#0b0b0d] border border-white/5 hover:border-[#D97736]/50 transition-colors group">
                  <Phone size={26} className="text-[#D97736]" />
                  <div>
                    <div className="text-sm text-[#A1A1AA]">Telefono</div>
                    <div className="font-medium group-hover:text-[#D97736] transition-colors">{s.phone}</div>
                  </div>
                </a>
              </div>
            </div>
          </Reveal>

          {/* Right forms */}
          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="bg-[#111113] border border-white/5 rounded-sm p-8 lg:p-10">
              <div className="flex gap-2 mb-8 bg-[#0b0b0d] p-1.5 rounded-sm border border-white/5 w-full sm:w-fit">
                <button
                  data-testid="tab-preventivo"
                  onClick={() => setTab("preventivo")}
                  className={`flex-1 sm:flex-none px-6 py-2.5 rounded-sm text-sm font-semibold transition-all ${tab === "preventivo" ? "bg-[#D97736] text-white" : "text-[#A1A1AA] hover:text-white"}`}
                >
                  Richiedi preventivo
                </button>
                <button
                  data-testid="tab-call"
                  onClick={() => setTab("call")}
                  className={`flex-1 sm:flex-none px-6 py-2.5 rounded-sm text-sm font-semibold transition-all ${tab === "call" ? "bg-[#D97736] text-white" : "text-[#A1A1AA] hover:text-white"}`}
                >
                  Prenota una call
                </button>
              </div>
              {tab === "preventivo" ? <PreventivoForm /> : <CallForm />}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
